// Synthesized stadium sounds (Web Audio, no asset files).
let ctx = null;
let noiseBuf = null;
let ambience = null;
const MUTE_KEY = "fc_mute";

export const isMuted = () => localStorage.getItem(MUTE_KEY) === "1";

const ac = () => {
  if (isMuted()) return null;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    if (!noiseBuf) {
      noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const d = noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    return ctx;
  } catch (e) {
    return null;
  }
};

const noise = (c, { type = "bandpass", freq = 700, q = 0.8, loop = false }) => {
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  src.loop = loop;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.value = 0;
  src.connect(f).connect(g).connect(c.destination);
  return { src, g, f };
};

const envelope = (g, t, peak, attack, decay) => {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
};

// Big crowd roar (goal / great save). size 0..1.
export const roar = (size = 1) => {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  [{ freq: 650, q: 0.6 }, { freq: 1400, q: 0.9 }, { freq: 320, q: 0.7 }].forEach((layer, i) => {
    const n = noise(c, layer);
    envelope(n.g, t, (0.32 - i * 0.07) * size, 0.22, 2.4 * size + 0.6);
    n.f.frequency.linearRampToValueAtTime(layer.freq * 1.25, t + 0.5);
    n.src.start(t);
    n.src.stop(t + 3.5);
  });
};

// Disappointed "ooh" from the crowd.
export const groan = () => {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const n = noise(c, { freq: 420, q: 1.2 });
  envelope(n.g, t, 0.18, 0.15, 1.1);
  n.f.frequency.setValueAtTime(520, t);
  n.f.frequency.exponentialRampToValueAtTime(260, t + 1.2);
  n.src.start(t);
  n.src.stop(t + 1.5);
};

// Referee pea-whistle trill. blasts > 1 for full-time.
export const whistle = (blasts = 1) => {
  const c = ac();
  if (!c) return;
  for (let b = 0; b < blasts; b++) {
    const t = c.currentTime + b * 0.55;
    const len = blasts > 1 && b === blasts - 1 ? 0.75 : 0.38;
    const g = c.createGain();
    g.connect(c.destination);
    envelope(g, t, 0.12, 0.02, len);
    [2850, 3050].forEach((f) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const lfo = c.createOscillator();
      const lg = c.createGain();
      lfo.frequency.value = 32;
      lg.gain.value = 140;
      lfo.connect(lg).connect(o.frequency);
      o.connect(g);
      o.start(t);
      lfo.start(t);
      o.stop(t + len + 0.05);
      lfo.stop(t + len + 0.05);
    });
  }
};

// Boot on ball.
export const kick = () => {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.frequency.setValueAtTime(150, t);
  o.frequency.exponentialRampToValueAtTime(45, t + 0.14);
  envelope(g, t, 0.5, 0.005, 0.16);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + 0.2);
  const n = noise(c, { type: "highpass", freq: 2500, q: 0.5 });
  envelope(n.g, t, 0.12, 0.002, 0.04);
  n.src.start(t);
  n.src.stop(t + 0.08);
};

// Ball rippling the net.
export const net = () => {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const n = noise(c, { type: "highpass", freq: 1800, q: 0.4 });
  envelope(n.g, t, 0.22, 0.01, 0.35);
  n.src.start(t);
  n.src.stop(t + 0.45);
  const o = c.createOscillator();
  const g = c.createGain();
  o.frequency.value = 85;
  envelope(g, t, 0.25, 0.005, 0.2);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + 0.25);
};

// Gloves palming it away.
export const parry = () => {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const n = noise(c, { type: "bandpass", freq: 900, q: 1.5 });
  envelope(n.g, t, 0.35, 0.003, 0.09);
  n.src.start(t);
  n.src.stop(t + 0.15);
};

// Low crowd murmur loop while the shootout is live.
export const startAmbience = () => {
  const c = ac();
  if (!c || ambience) return;
  const n = noise(c, { freq: 520, q: 0.5, loop: true });
  n.g.gain.setValueAtTime(0.0001, c.currentTime);
  n.g.gain.exponentialRampToValueAtTime(0.045, c.currentTime + 1.5);
  const lfo = c.createOscillator();
  const lg = c.createGain();
  lfo.frequency.value = 0.18;
  lg.gain.value = 0.015;
  lfo.connect(lg).connect(n.g.gain);
  n.src.start();
  lfo.start();
  ambience = { n, lfo };
};

export const stopAmbience = () => {
  if (!ambience) return;
  try {
    ambience.n.src.stop();
    ambience.lfo.stop();
  } catch (e) { /* already stopped */ }
  ambience = null;
};

export const setMuted = (m) => {
  localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  if (m) stopAmbience();
};
